import {
  Box,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
  Flex,
} from '@chakra-ui/react'
import React, { useState, useEffect } from 'react'

interface ExtractionsSectionProps {
  removedTeeth: string
  setRemovedTeeth: (value: string) => void
  firstToothExtractedAge: string
  setFirstToothExtractedAge: (value: string) => void
  cavitChecked: string
  setCavitChecked: (value: string) => void
  cavitations: string
  setCavitations: (value: string) => void
  computedCaviationExposure: string | number
  isSaved: boolean
  adjustedCavitations?: string
  hasCavitationAdjustments?: boolean
}

const ExtractionsSection: React.FC<ExtractionsSectionProps> = ({
  removedTeeth,
  setRemovedTeeth,
  firstToothExtractedAge,
  setFirstToothExtractedAge,
  cavitChecked,
  setCavitChecked,
  cavitations,
  setCavitations,
  computedCaviationExposure,
  isSaved,
  adjustedCavitations,
  hasCavitationAdjustments,
}) => {
  // Helper function to determine if a field should be disabled
  // Only disable fields when the form has been saved AND the field has a value
  const isFieldDisabled = (value: string) => {
    return isSaved && value !== ''
  }

  // Helper function for Select component
  const isSelectDisabled = () => {
    return isSaved && cavitChecked !== ''
  }

  // For cavitations section, handle the special case
  const isCavitationsFieldDisabled = () => {
    if (cavitChecked !== 'Yes') return true

    const fieldValue = isSaved ? adjustedCavitations || '' : cavitations
    return isSaved && fieldValue !== ''
  }

  // Internal state for handling input changes
  const [workingRemovedTeeth, setWorkingRemovedTeeth] = useState(removedTeeth)
  const [workingFirstToothExtractedAge, setWorkingFirstToothExtractedAge] =
    useState(firstToothExtractedAge)
  const [workingCavitChecked, setWorkingCavitChecked] = useState(cavitChecked)
  const [workingCavitations, setWorkingCavitations] = useState(cavitations)

  // Update internal state when props change (for initial load and adjustments)
  useEffect(() => {
    setWorkingRemovedTeeth(removedTeeth)
  }, [removedTeeth])

  useEffect(() => {
    setWorkingFirstToothExtractedAge(firstToothExtractedAge)
  }, [firstToothExtractedAge])

  useEffect(() => {
    setWorkingCavitChecked(cavitChecked)
  }, [cavitChecked])

  useEffect(() => {
    if (isSaved && cavitChecked === 'Yes' && adjustedCavitations) {
      setWorkingCavitations(adjustedCavitations)
    } else {
      setWorkingCavitations(cavitations)
    }
  }, [isSaved, cavitChecked, cavitations, adjustedCavitations])

  return (
    <>
      <Text fontSize="lg" fontWeight="bold" mt={8}>
        Extractions/Potential Cavitations
      </Text>
      <SimpleGrid columns={1} spacing={4} mt={4}>
        <Flex>
          <FormLabel w="50%">
            1. How many total teeth were removed in your lifetime? (include
            wisdom teeth)
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter total number"
            value={workingRemovedTeeth}
            onChange={(e) => {
              if (!isFieldDisabled(removedTeeth)) {
                setWorkingRemovedTeeth(e.target.value)
              }
            }}
            onBlur={() => setRemovedTeeth(workingRemovedTeeth)}
            disabled={isFieldDisabled(removedTeeth)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            2. Estimated age you had your first tooth extracted
          </FormLabel>
          <Input
            w="25%"
            type="number"
            placeholder="Enter age"
            value={workingFirstToothExtractedAge}
            onChange={(e) => {
              if (!isFieldDisabled(firstToothExtractedAge)) {
                setWorkingFirstToothExtractedAge(e.target.value)
              }
            }}
            onBlur={() =>
              setFirstToothExtractedAge(workingFirstToothExtractedAge)
            }
            disabled={isFieldDisabled(firstToothExtractedAge)}
          />
        </Flex>
        <Flex>
          <FormLabel w="50%">
            3. Have you had your mouth checked for cavitations? (Yes/No)
          </FormLabel>
          <Select
            w="25%"
            placeholder="Select option"
            value={workingCavitChecked}
            onChange={(e) => {
              if (!isSelectDisabled()) {
                setWorkingCavitChecked(e.target.value)
                setCavitChecked(e.target.value)
              }
            }}
            disabled={isSelectDisabled()}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </Select>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            4. If yes, how many cavitations do you still have
          </FormLabel>
          <Box w="25%">
            <Input
              type="number"
              placeholder="Enter number"
              disabled={isCavitationsFieldDisabled()}
              value={workingCavitations}
              onChange={(e) => {
                if (!isCavitationsFieldDisabled()) {
                  setWorkingCavitations(e.target.value)
                }
              }}
              onBlur={() => {
                if (cavitChecked === 'Yes') {
                  setCavitations(workingCavitations)
                }
              }}
            />
            {isSaved && cavitChecked === 'Yes' && hasCavitationAdjustments && (
              <Text fontSize="xs" color="blue.600" mt={1}>
                (Adjusted based on dental procedures)
              </Text>
            )}
          </Box>
        </Flex>
        <Flex>
          <FormLabel w="50%">
            <em>Estimated cavitation exposure</em>
          </FormLabel>
          <Text w="25%" fontWeight="bold" pl={3}>
            {computedCaviationExposure || '-'}
          </Text>
        </Flex>
      </SimpleGrid>
    </>
  )
}

export default ExtractionsSection
